import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { useSplitContext } from '../contexts/split/split';
import { Logger } from '../../common/Logger';

/**
 * Custom hook for feature flags with dynamic configuration support.
 * Returns both the treatment and any configuration attached to it.
 *
 * @param flagName - The name of the feature flag
 * @param expectedTreatment - The treatment to check for (defaults to 'on')
 * @param attributes - Additional attributes for targeting (optional)
 * @returns Object containing flag state, configuration, and utilities
 *
 * @example
 * ```tsx
 * const { isEnabled, treatment, config } = useFeatureFlag('search-algorithm');
 *
 * return (
 *   <SearchComponent
 *     enabled={isEnabled}
 *     maxRetries={config?.retries || 3}
 *     timeout={config?.timeout || 5000}
 *     algorithm={config?.algorithm || 'default'}
 *   />
 * );
 * ```
 */
export const useFeatureFlag = (
  flagName: string,
  expectedTreatment: string = 'on',
  attributes?: Record<string, any>
) => {
  const { businessId, userId, isReady } = useSplitContext();

  // Add error handling around Split.io calls
  let treatments: any = {};
  let splitIsReady = false;
  let error: Error | null = null;

  try {
    const splitResult = useSplitTreatments({
      names: [flagName],
      attributes: {
        ...(businessId && { businessId }),
        ...(userId && { userId }),
        ...attributes,
      }
    });

    treatments = splitResult.treatments || {};
    splitIsReady = splitResult.isReady;
  } catch (e) {
    error = e as Error;
    console.warn(`[Split.io] Error loading feature flag "${flagName}":`, e);

    // Log to CloudWatch
    Logger.error({
      error: (e as Error).message,
      stack: (e as Error).stack,
      flagName,
      expectedTreatment,
      attributes,
      source: 'useFeatureFlag',
      url: window.location.href,
    }, 'useFeatureFlag');
  }

  const treatment = treatments[flagName]?.treatment || 'control';
  const rawConfig = treatments[flagName]?.config || null;

  // Parse config if it's a JSON string
  let config = null;
  if (rawConfig) {
    try {
      config = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
    } catch (e) {
      console.warn(`[Split.io] Failed to parse config for flag "${flagName}":`, e);
      config = null;
    }
  }

  // Safe fallback - only enable if SDK is ready AND treatment matches
  const isEnabled = splitIsReady && treatment === expectedTreatment;

  return {
    /** Whether the feature flag is enabled (matches expected treatment) */
    isEnabled,
    /** The actual treatment received from Split.io */
    treatment,
    /** Dynamic configuration attached to the treatment */
    config,
    /** Whether the Split.io SDK is ready */
    isReady: splitIsReady,
    /** Any error that occurred */
    error,
    /** Safe way to check if feature is enabled with fallback */
    isEnabledWithFallback: (fallback: boolean = false) => {
      if (error) return fallback;
      return splitIsReady ? isEnabled : fallback;
    }
  };
};

/**
 * Hook to get a specific configuration value from a feature flag with safe fallbacks
 */
export const useDynamicConfig = <T = any>(
  flagName: string,
  configKey: string,
  defaultValue: T,
  attributes?: Record<string, any>
): T => {
  const { config, error, isReady } = useFeatureFlag(flagName, undefined, attributes);

  // Return default value if there's an error or SDK not ready
  if (error || !isReady) {
    return defaultValue;
  }

  return config?.[configKey] ?? defaultValue;
};

/**
 * Hook to get the entire configuration object from a feature flag with safe fallbacks
 */
export const useDynamicConfigObject = <T = Record<string, any>>(
  flagName: string,
  defaultConfig: T,
  attributes?: Record<string, any>
): T => {
  const { config, error, isReady } = useFeatureFlag(flagName, undefined, attributes);

  // Return default config if there's an error or SDK not ready
  if (error || !isReady) {
    return defaultConfig;
  }

    return config && typeof config === 'object' && !Array.isArray(config)
    ? { ...defaultConfig, ...(config as Record<string, any>) }
    : defaultConfig;
};

/**
 * Hook specifically for business-level feature flags
 * Uses businessId as the primary targeting key
 */
export const useBusinessFeatureFlag = (
  flagName: string,
  expectedTreatment: string = 'on',
  attributes?: Record<string, any>
) => {
  const { businessId } = useSplitContext();

  return useFeatureFlag(flagName, expectedTreatment, {
    businessId,
    ...attributes,
  });
};

/**
 * Hook that provides multiple feature flags at once with error handling
 */
export const useFeatureFlags = (
  flags: Array<{ name: string; expectedTreatment?: string }>,
  attributes?: Record<string, any>
) => {
  const { businessId, userId, isReady } = useSplitContext();

  let treatments: any = {};
  let splitIsReady = false;
  let error: Error | null = null;

  try {
    const flagNames = flags.map(f => f.name);
    const splitResult = useSplitTreatments({
      names: flagNames,
      attributes: {
        ...(businessId && { businessId }),
        ...(userId && { userId }),
        ...attributes,
      }
    });

    treatments = splitResult.treatments || {};
    splitIsReady = splitResult.isReady;
  } catch (e) {
    error = e as Error;
    console.warn('[Split.io] Error loading feature flags:', e);
  }

  const results = flags.reduce((acc, flag) => {
    const treatment = treatments[flag.name]?.treatment || 'control';
    const rawConfig = treatments[flag.name]?.config || null;

    // Parse config if it's a JSON string
    let config = null;
    if (rawConfig) {
      try {
        config = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
      } catch (e) {
        console.warn(`[Split.io] Failed to parse config for flag "${flag.name}":`, e);
        config = null;
      }
    }

    const expectedTreatment = flag.expectedTreatment || 'on';
    const isEnabled = splitIsReady && treatment === expectedTreatment;

    acc[flag.name] = {
      isEnabled,
      treatment,
      config,
      isEnabledWithFallback: (fallback: boolean = false) => {
        if (error) return fallback;
        return splitIsReady ? isEnabled : fallback;
      }
    };

    return acc;
  }, {} as Record<string, any>);

  return {
    flags: results,
    isReady: splitIsReady,
    error,
  };
};